// EXTERNAL DEPENDENCIES
import { useLocation, useNavigate } from 'react-router'

// HELPERS
import {
  useBusinessProfileReduxState,
  useModularSellerViewPageContent,
} from '@/screens/business-profile-page/hooks'
import { useFormattedAddresses } from '@/hooks'
import { trackClick } from '@/utils'

// FPTI
import {
  BUSINESS_PROFILE_SELLER_VIEW_BUSINESS_INFO_CARD_FPTI,
  BUSINESS_PROFILE_SELLER_VIEW_DESCRIPTION_CARD_FPTI,
} from '../../../constants/fpti'

// TYPES
import { PortableAddress } from '@/types'

/**
 * Get the relevant props for the business description row display
 */
function useDescriptionRow() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const getContent = useModularSellerViewPageContent()
  const descriptionLabel = getContent('ownerProfileScreenDescriptionLabel')
  const ownerProfileScreenDescriptionEmptyText = getContent(
    'ownerProfileScreenDescriptionEmpty',
  )
  const addButtonText = getContent('addButton')

  // REDUX
  const {
    uneditedFormattedData: { businessDescription },
  } = useBusinessProfileReduxState()

  return {
    label: descriptionLabel,
    description: businessDescription || ownerProfileScreenDescriptionEmptyText,
    icon: 'business',
    customRightLinkText: businessDescription ? null : addButtonText,
    onClick: () => {
      trackClick(BUSINESS_PROFILE_SELLER_VIEW_DESCRIPTION_CARD_FPTI)
      navigate(`${pathname}/edit/description`)
    },
  }
}

/**
 * Get the relevant props for the business info row display
 */
function useBizInfoRow() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const getContent = useModularSellerViewPageContent()
  const contactAndLocationLabel = getContent(
    'ownerProfileScreenContactLocationLabel',
  )

  // REDUX
  const {
    uneditedFormattedData: {
      contactInfo,
      businessAddress,
      profilePreferences: { locationDisplayPreference },
    },
  } = useBusinessProfileReduxState()

  const formattedAddresses = useFormattedAddresses(
    businessAddress as PortableAddress,
  )
  const displayedAddress = formattedAddresses[locationDisplayPreference]

  // HELPERS
  function getDisplayedContactInfo(
    contactInfo: {
      businessPhoneNumber?: string
      businessEmail?: string
      businessWebsiteUrl?: string
    },
    displayedAddress: string,
  ) {
    const displayedContactInfo = []

    if (contactInfo?.businessPhoneNumber) {
      displayedContactInfo.push(contactInfo.businessPhoneNumber)
    }
    if (contactInfo?.businessEmail) {
      displayedContactInfo.push(contactInfo.businessEmail)
    }
    if (contactInfo?.businessWebsiteUrl) {
      displayedContactInfo.push(contactInfo.businessWebsiteUrl)
    }
    const contactInfoString = displayedContactInfo.join(', ')

    if (contactInfoString && displayedAddress) {
      return `${contactInfoString}\n${displayedAddress}`
    }
    return contactInfoString || displayedAddress
  }

  return {
    label: contactAndLocationLabel,
    description: getDisplayedContactInfo(contactInfo, displayedAddress),
    icon: 'message-center',
    onClick: () => {
      navigate(`${pathname}/edit/bizinfo`)
      trackClick(BUSINESS_PROFILE_SELLER_VIEW_BUSINESS_INFO_CARD_FPTI)
    },
  }
}

export { useDescriptionRow, useBizInfoRow }
